import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import authService from '../services/auth.service';
import { useDispatch } from 'react-redux';
import { login as loginAction, logout as logoutAction } from '../store/features/authSlice';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // Start with loading true
  const initializationAttempted = useRef(false);
  const dispatch = useDispatch();

  console.log('🏗️ AuthProvider: Component rendered', { 
    user: user?.email || 'none', 
    loading,
    initializationAttempted: initializationAttempted.current 
  });

  // Initialize auth state - only call once
  const initializeAuth = async () => {
    if (initializationAttempted.current) {
      console.log('⚠️ AuthProvider: Initialization already attempted, skipping');
      return;
    }
    
    console.log('🚀 AuthProvider: Starting auth initialization');
    initializationAttempted.current = true;
    setLoading(true);
    
    try {
      // Check for OAuth success parameter
      const urlParams = new URLSearchParams(window.location.search);
      const oauthSuccess = urlParams.get('oauth_success');
      
      if (oauthSuccess === 'true') {
        console.log('🔑 AuthProvider: OAuth success detected, getting user info...');
        
        // Clean up the URL parameter
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('oauth_success');
        window.history.replaceState({}, document.title, newUrl.pathname + newUrl.search + newUrl.hash);
      }
      
      // Check current user via session
      const currentUser = await authService.getCurrentUser();
      if (currentUser) {
        console.log('✅ AuthProvider: Auth initialization successful via session', currentUser);
        setUser(currentUser);
        dispatch(loginAction(currentUser));
      } else {
        console.log('❌ AuthProvider: No authenticated user found');
        setUser(null);
      }
    } catch (error) {
      console.log('❌ AuthProvider: Auth initialization failed', error.message);
      setUser(null);
    } finally {
      console.log('🔄 AuthProvider: Auth initialization complete, setting loading to false');
      setLoading(false);
    }
  };

  // Auto-initialize authentication on mount
  useEffect(() => {
    console.log('🔄 AuthProvider: useEffect triggered, calling initializeAuth');
    initializeAuth();
  }, []);

  const login = async (...args) => {
    const [email, password] = args;
    console.log('🔐 AuthProvider: Login method called');
    console.log('📧 AuthProvider: Email parameter:', email);
    setLoading(true);
    
    try {
      console.log('📞 AuthProvider: Calling authService.login()');
      const result = await authService.login(email, password);
      console.log('✅ AuthProvider: Login service call successful', result);
      
      if (result && result.user) {
        console.log('👤 AuthProvider: Setting user data', result.user);
        setUser(result.user);
        console.log('🎉 AuthProvider: Login complete - user set');
        dispatch(loginAction(result.user));
        
        return result;
      } else {
        console.error('⚠️ AuthProvider: Login result missing user data', result);
        throw new Error('Login response missing user data');
      }
    } catch (error) {
      console.error('❌ AuthProvider: Login failed', error);
      setUser(null);
      dispatch(logoutAction());
      throw error;
    } finally {
      console.log('🔄 AuthProvider: Login process complete, setting loading to false');
      setLoading(false);
    }
  };

  const logout = async () => {
    console.log('🚪 AuthProvider: Logout method called');
    setLoading(true);
    
    try {
      console.log('📞 AuthProvider: Calling authService.logout()');
      await authService.logout();
      console.log('✅ AuthProvider: Logout successful');
      setUser(null);
      console.log('👤 AuthProvider: User cleared from state');
      dispatch(logoutAction());
    } catch (error) {
      console.error('❌ AuthProvider: Logout failed', error);
      // Clear user anyway on logout error
      setUser(null);
      dispatch(logoutAction());
    } finally {
      console.log('🔄 AuthProvider: Logout process complete, setting loading to false');
      setLoading(false);
    }
  };

  const register = async (email, password) => {
    console.log('👤 AuthProvider: Register method called', { email });
    setLoading(true);
    
    try {
      console.log('📞 AuthProvider: Calling authService.register()');
      const result = await authService.register(email, password);
      console.log('✅ AuthProvider: Registration successful', result);
      // Don't set user here - they need to confirm email first
      return result;
    } catch (error) {
      console.error('❌ AuthProvider: Registration failed', error);
      throw error;
    } finally {
      console.log('🔄 AuthProvider: Registration process complete, setting loading to false');
      setLoading(false);
    }
  };

  const resendConfirmation = async (email) => {
    const response = await authService.resendConfirmation(email);
    return response;
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    resendConfirmation,
    initializeAuth,
    isAuthenticated: !!user
  };

  console.log('📦 AuthProvider: Providing context value', { 
    hasUser: !!user, 
    userEmail: user?.email,
    loading 
  });

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 